import React, { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  AppContainer,
  Header,
  Navigation,
  AgentPanel
} from '@templatean/shared/components'
import { useAuth } from '@templatean/shared/hooks'
import type { AgentAction } from '@templatean/shared/types'
import { outlookAgentPanelConfig } from '../config/agentConfig'
import { outlookService } from '../services/outlookService'

const AgentPage: React.FC = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [statusMessage, setStatusMessage] = useState<string | null>(null)

  /**
   * Handle actions from the AgentPanel
   * This callback processes insert, replace, and other actions from AI responses
   * for Outlook email composition
   */
  const handleAgentAction = useCallback(
    async (action: AgentAction) => {
      try {
        setStatusMessage(null)

        switch (action.actionType) {
          case 'insert':
            // Insert content into the email body
            if (action.payload) {
              await outlookService.insertHtml(action.payload, 'end')
              setStatusMessage('Content inserted into email')
            }
            break

          case 'replace':
            // Replace the email body with the payload
            if (action.payload) {
              await outlookService.insertHtml(action.payload, 'replace')
              setStatusMessage('Email content replaced')
            }
            break

          case 'navigate':
            // Navigate to a specific location/page
            // This could be extended for navigating to attachments, etc.
            console.log('Navigate action:', action.payload)
            if (action.payload === 'templates') {
              navigate('/email-templates')
            } else if (action.payload === 'composer') {
              navigate('/email-composer')
            }
            break

          case 'apply':
            // Apply formatting or set subject/recipients
            if (action.payload) {
              try {
                const data = JSON.parse(action.payload)
                if (data.subject) {
                  await outlookService.setSubject(data.subject)
                  setStatusMessage('Subject applied')
                }
                if (data.recipients && Array.isArray(data.recipients)) {
                  await outlookService.addRecipients(data.recipients)
                  setStatusMessage('Recipients added')
                }
              } catch {
                // If not JSON, treat as subject
                await outlookService.setSubject(action.payload)
                setStatusMessage('Subject set')
              }
            }
            break

          case 'ignore':
            // User chose to ignore the suggestion
            console.log('Ignored action:', action.id)
            setStatusMessage('Action dismissed')
            break

          default:
            console.warn('Unknown action type:', action.actionType)
        }
      } catch (error) {
        console.error('Failed to execute agent action:', error)
        setStatusMessage('Failed to execute action')
      }
    },
    [navigate]
  )

  const navItems = [
    { id: 'home', label: 'Home', icon: 'home', onClick: () => navigate('/') },
    { id: 'agent', label: 'Agent', icon: 'bot', active: true },
    { id: 'email-templates', label: 'Templates', icon: 'mail', onClick: () => navigate('/email-templates') },
    { id: 'email-composer', label: 'AI Composer', icon: 'sparkles', onClick: () => navigate('/email-composer') },
  ]

  return (
    <AppContainer>
      <Header
        title="AI Agent"
        subtitle={user?.fullName || 'Outlook Assistant'}
      />

      <Navigation items={navItems} />

      <div className="agent-page-content">
        {statusMessage && (
          <div className="status-message">
            {statusMessage}
          </div>
        )}

        <AgentPanel
          config={outlookAgentPanelConfig}
          onAction={handleAgentAction}
        />
      </div>
    </AppContainer>
  )
}

export default AgentPage
